import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Save, Send, Plus, Trash2, ChevronDown, ChevronUp,
  Building2, Package, Percent, FileText, X, Mail, Files, Upload, Paperclip, Download, File
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getQuote, createQuote, updateQuote, sendQuote,
  getClients, getProducts, getEntities, getTemplates,
  getAttachments, uploadAttachment, deleteAttachment,
  formatMoney, getApiError 
} from '../api';
import { useLanguage } from '../i18n';

const CATEGORIES = ['Knygomatai', 'Paslaugos', 'Grąžinimo dėžės', 'Detektoriai', 'Kita'];

const QdQuoteEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language: uiLang } = useLanguage();
  const isNew = !id || id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  
  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Form state
  const [entityId, setEntityId] = useState('uab');
  const [clientId, setClientId] = useState('');
  const [language, setLanguage] = useState('lt');
  const [items, setItems] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [vatRate, setVatRate] = useState(21);
  const [validityDays, setValidityDays] = useState(90);
  const [conditions, setConditions] = useState('');
  const [status, setStatus] = useState('draft');
  const [quoteNumber, setQuoteNumber] = useState('');
  
  useEffect(() => {
    loadData();
  }, [id]);
  
  // Check if a template was passed via navigation
  useEffect(() => {
    if (location.state?.template && isNew) {
      applyTemplate(location.state.template);
    }
  }, [location.state]);
  
  const loadData = async () => {
    try {
      const [clientsRes, productsRes, entitiesRes, templatesRes] = await Promise.all([
        getClients(),
        getProducts(),
        getEntities(),
        getTemplates()
      ]);
      
      setClients(clientsRes.data);
      setProducts(productsRes.data.filter(p => p.active !== false));
      setEntities(entitiesRes.data);
      setTemplates(templatesRes.data);
      
      if (!isNew) {
        const quoteRes = await getQuote(id);
        const quote = quoteRes.data;
        
        setEntityId(quote.entity_id);
        setClientId(quote.client_id);
        setLanguage(quote.language);
        setItems(quote.items || []);
        setDiscountPct(quote.discount_pct || 0);
        setDiscountReason(quote.discount_reason || '');
        setVatRate(quote.vat_rate || 21);
        setValidityDays(quote.validity_days || 90);
        setConditions(quote.conditions || '');
        setStatus(quote.status);
        setQuoteNumber(quote.number);
        
        // Load attachments
        loadAttachments(id);
      }
    } catch (error) {
      toast.error(getApiError(error));
      navigate('/quote/quotes');
    } finally {
      setLoading(false);
    }
  };
  
  const loadAttachments = async (quoteId) => {
    try {
      const response = await getAttachments(quoteId);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    }
  };
  
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const maxSize = 25 * 1024 * 1024; // 25MB
    
    if (file.size > maxSize) {
      toast.error('Failas per didelis. Maksimalus dydis yra 25 MB');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      await uploadAttachment(id, file, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(progress);
      });
      
      toast.success(`Failas "${file.name}" įkeltas`);
      loadAttachments(id);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleDeleteAttachment = async (attachment) => {
    if (!confirm(`Ar tikrai norite ištrinti "${attachment.original_filename}"?`)) return;
    
    try {
      await deleteAttachment(id, attachment.id);
      toast.success('Failas ištrintas');
      loadAttachments(id);
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const applyTemplate = (template) => {
    setEntityId(template.entity_id || 'uab');
    setLanguage(template.language || 'lt');
    setItems(template.items || []);
    setDiscountPct(template.discount_pct || 0);
    setVatRate(template.vat_rate || 21);
    setValidityDays(template.validity_days || 90);
    setConditions(template.conditions || '');
    setShowTemplateSelector(false);
    toast.success(`Šablonas "${template.name}" pritaikytas`);
  };
  
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const discount = subtotal * (discountPct / 100);
    const net = subtotal - discount;
    const vat = net * (vatRate / 100);
    const total = net + vat;
    
    return { subtotal, discount, net, vat, total };
  };
  
  const addProduct = (product) => {
    const existing = items.find(i => i.ref === product.ref);
    if (existing) {
      setItems(items.map(i => 
        i.ref === product.ref 
          ? { ...i, quantity: i.quantity + 1, total: (i.quantity + 1) * i.unit_price }
          : i
      ));
    } else {
      setItems([...items, {
        ref: product.ref,
        name: product.name,
        quantity: 1,
        unit_price: product.price,
        total: product.price
      }]);
    }
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setItems(newItems);
  };
  
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };
  
  const addFreeItem = () => {
    setItems([...items, {
      ref: '',
      name: '',
      quantity: 1,
      unit_price: 0,
      total: 0
    }]);
  };
  
  const handleSave = async (e) => {
    e?.preventDefault();
    
    if (!clientId) {
      toast.error('Pasirinkite klientą');
      return;
    }
    
    if (items.length === 0) {
      toast.error('Pridėkite bent vieną prekę');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        entity_id: entityId,
        client_id: clientId,
        language,
        items,
        discount_pct: discountPct,
        discount_reason: discountReason,
        vat_rate: vatRate,
        validity_days: validityDays,
        conditions
      };
      
      if (isNew) {
        const response = await createQuote(data);
        toast.success(`Pasiūlymas sukurtas: ${response.data.number}`);
        navigate(`/quote/quotes/${response.data.id}`);
      } else {
        await updateQuote(id, data);
        toast.success('Pasiūlymas išsaugotas');
        loadData();
      }
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  const totals = calculateTotals();
  const isEditable = status === 'draft';
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
      </div>
    );
  }
  
  const selectedClient = clients.find(c => c.id === clientId);
  
  // Group products by category
  const productsByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = products.filter(p => p.category === cat);
    return acc;
  }, {});
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/quote/quotes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? t('quoteEditor.newQuote') : quoteNumber}
            </h1>
            {!isNew && (
              <span className={`
                inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium
                ${status === 'draft' ? 'bg-gray-100 text-gray-700' : ''}
                ${status === 'sent' ? 'bg-blue-100 text-blue-700' : ''}
                ${status === 'accepted' ? 'bg-green-100 text-green-700' : ''}
                ${status === 'expired' ? 'bg-red-100 text-red-700' : ''}
              `}>
                {t(`quotes.status.${status}`)}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {isNew && templates.length > 0 && (
            <button
              onClick={() => setShowTemplateSelector(true)}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Files size={18} />
              {t('quoteEditor.templates.button')}
            </button>
          )}
          {isEditable && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#14233F] text-white rounded-lg hover:bg-[#1a3050] transition-colors disabled:opacity-50"
              data-testid="save-quote-button"
            >
              <Save size={18} />
              {saving ? t('quoteEditor.messages.saving') : t('common.save')}
            </button>
          )}
          {!isNew && status === 'draft' && (
            <button
              onClick={() => setShowSendModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
              data-testid="send-quote-button"
            >
              <Send size={18} />
              {t('common.send')}
            </button>
          )}
        </div>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 size={20} className="text-[#0F7D70]" />
              {t('quoteEditor.basicInfo')}
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quoteEditor.seller')}
                </label>
                <select
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  disabled={!isEditable}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                >
                  {entities.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quoteEditor.client')} *
                </label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  disabled={!isEditable}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                  data-testid="client-select"
                >
                  <option value="">-- {t('quoteEditor.selectClient')} --</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quoteEditor.documentLanguage')}
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={!isEditable}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="lt">Lietuvių (LT)</option>
                  <option value="en">English (EN)</option>
                  <option value="lv">Latviešu (LV)</option>
                  <option value="et">Eesti (ET)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quoteEditor.validityDays')}
                </label>
                <input
                  type="number"
                  value={validityDays}
                  onChange={(e) => setValidityDays(parseInt(e.target.value) || 90)}
                  disabled={!isEditable}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                />
              </div>
            </div>
            
            {selectedClient && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>Adresas:</strong> {selectedClient.address || '-'}</p>
                <p><strong>PVM kodas:</strong> {selectedClient.vat_number || '-'}</p>
                <p><strong>Kontaktas:</strong> {selectedClient.contact_name || '-'} ({selectedClient.contact_email || '-'})</p>
              </div>
            )}
          </div>
          
          {/* Items */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Package size={20} className="text-[#0F7D70]" />
                {t('quoteEditor.items.title')}
              </h2>
              {isEditable && (
                <button
                  onClick={addFreeItem}
                  className="text-sm text-[#0F7D70] hover:underline flex items-center gap-1"
                >
                  <Plus size={16} />
                  {t('quoteEditor.items.freeLine')}
                </button>
              )}
            </div>
            
            {items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package size={40} className="mx-auto mb-2 opacity-30" />
                <p>{t('quoteEditor.items.addFromCatalog')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-12 gap-2">
                      <input
                        type="text"
                        value={item.ref}
                        onChange={(e) => updateItem(index, 'ref', e.target.value)}
                        placeholder={t('quoteEditor.items.code')}
                        disabled={!isEditable}
                        className="col-span-2 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0F7D70] disabled:bg-gray-100"
                      />
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        placeholder="Pavadinimas"
                        disabled={!isEditable}
                        className="col-span-5 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0F7D70] disabled:bg-gray-100"
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        min="1"
                        disabled={!isEditable}
                        className="col-span-1 px-2 py-1.5 text-sm border border-gray-300 rounded text-center focus:ring-1 focus:ring-[#0F7D70] disabled:bg-gray-100"
                      />
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        step="0.01"
                        disabled={!isEditable}
                        className="col-span-2 px-2 py-1.5 text-sm border border-gray-300 rounded text-right focus:ring-1 focus:ring-[#0F7D70] disabled:bg-gray-100"
                      />
                      <div className="col-span-2 px-2 py-1.5 text-sm text-right font-medium">
                        {formatMoney(item.total)}
                      </div>
                    </div>
                    {isEditable && (
                      <button
                        onClick={() => removeItem(index)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Discount & Conditions */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Percent size={20} className="text-[#0F7D70]" />
              {t('quoteEditor.discount.title')}
            </h2>
            
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quoteEditor.discount.discountPct')}
                </label>
                <input
                  type="number"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.1"
                  disabled={!isEditable}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quoteEditor.discount.vatPct')}
                </label>
                <input
                  type="number"
                  value={vatRate}
                  onChange={(e) => setVatRate(parseFloat(e.target.value) || 21)}
                  disabled={!isEditable}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quoteEditor.discount.discountReason')}
                </label>
                <input
                  type="text"
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder={t('quoteEditor.discount.discountReasonPlaceholder')}
                  disabled={!isEditable}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('quoteEditor.discount.conditions')}
              </label>
              <textarea
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                rows={3}
                disabled={!isEditable}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                placeholder={t('quoteEditor.discount.conditionsPlaceholder')}
              />
            </div>
          </div>
          
          {/* Attachments Section */}
          {!isNew && (
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Paperclip size={20} className="text-[#0F7D70]" />
                {t('quoteEditor.attachments.title')}
              </h2>
              
              {/* Upload Zone */}
              {isEditable && (
                <div className="mb-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    data-testid="attachment-file-input"
                  />
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full border-2 border-dashed border-gray-300 hover:border-[#0F7D70] rounded-lg p-6 text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="upload-attachment-button"
                  >
                    {uploading ? (
                      <div className="space-y-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0F7D70] border-t-transparent mx-auto" />
                        <p className="text-sm text-gray-600">{t('quoteEditor.attachments.uploading')} {uploadProgress}%</p>
                        <div className="w-full bg-gray-200 rounded-full h-2 max-w-xs mx-auto">
                          <div 
                            className="bg-[#0F7D70] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload size={32} className="mx-auto text-gray-400" />
                        <p className="text-sm font-medium text-gray-700">
                          {t('quoteEditor.attachments.uploadPrompt')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t('quoteEditor.attachments.uploadFormats')}
                        </p>
                      </div>
                    )}
                  </button>
                </div>
              )}
              
              {/* Attachments List */}
              {attachments.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <File size={40} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('quoteEditor.attachments.noFiles')}</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="attachments-list">
                  {attachments.map((attachment) => (
                    <div 
                      key={attachment.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 w-10 h-10 bg-[#0F7D70]/10 rounded-lg flex items-center justify-center">
                          <File size={20} className="text-[#0F7D70]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {attachment.original_filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(attachment.file_size)} • {new Date(attachment.created_at).toLocaleDateString('lt-LT')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <a
                          href={attachment.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-[#0F7D70] hover:bg-[#0F7D70]/10 rounded-lg transition-colors"
                          title="Atsisiųsti"
                        >
                          <Download size={18} />
                        </a>
                        {isEditable && (
                          <button
                            onClick={() => handleDeleteAttachment(attachment)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Ištrinti"
                            data-testid={`delete-attachment-${attachment.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {attachments.length > 0 && (
                <p className="text-xs text-gray-500 mt-3">
                  {attachments.length} {t('quoteEditor.attachments.fileCount')} • {t('quoteEditor.attachments.filesNote')}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Totals */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h2 className="font-semibold text-gray-900 mb-4">{t('quoteEditor.totals.title')}</h2>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('quoteEditor.totals.subtotal')}:</span>
                <span>{formatMoney(totals.subtotal)}</span>
              </div>
              {discountPct > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>{t('quoteEditor.totals.discount')} ({discountPct}%):</span>
                  <span>-{formatMoney(totals.discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">{t('quoteEditor.totals.netAmount')}:</span>
                <span>{formatMoney(totals.net)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('quoteEditor.totals.vat')} ({vatRate}%):</span>
                <span>{formatMoney(totals.vat)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200 text-lg font-bold">
                <span>{t('quoteEditor.totals.totalWithVat')}:</span>
                <span className="text-[#0F7D70]">{formatMoney(totals.total)}</span>
              </div>
            </div>
          </div>
          
          {/* Catalog */}
          {isEditable && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                onClick={() => setCatalogOpen(!catalogOpen)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Package size={20} className="text-[#0F7D70]" />
                  {t('quoteEditor.catalog.title')}
                </h2>
                {catalogOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              
              {catalogOpen && (
                <div className="border-t border-gray-100 max-h-96 overflow-y-auto">
                  {CATEGORIES.map(category => {
                    const categoryProducts = productsByCategory[category];
                    if (!categoryProducts?.length) return null;
                    
                    return (
                      <div key={category}>
                        <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                          {category}
                        </div>
                        {categoryProducts.map(product => (
                          <button
                            key={product.id}
                            onClick={() => addProduct(product)}
                            className="w-full px-4 py-2 text-left hover:bg-[#0F7D70]/5 transition-colors border-b border-gray-50"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{product.ref}</p>
                                <p className="text-xs text-gray-500 line-clamp-2">{product.name}</p>
                              </div>
                              <span className="text-sm font-medium text-[#0F7D70] whitespace-nowrap ml-2">
                                {formatMoney(product.price)}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Send Modal */}
      {showSendModal && (
        <SendQuoteModal
          quote={{ id, number: quoteNumber, client_snapshot: selectedClient }}
          onClose={() => setShowSendModal(false)}
          onSent={() => {
            setShowSendModal(false);
            loadData();
          }}
        />
      )}
      
      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <TemplateSelectorModal
          templates={templates}
          onSelect={applyTemplate}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}
    </div>
  );
};

// Template Selector Modal
const TemplateSelectorModal = ({ templates, onSelect, onClose }) => {
  const calculateTotal = (items) => items.reduce((sum, item) => sum + (item.total || 0), 0);
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Files size={20} className="text-[#0F7D70]" />
            Pasirinkite šabloną
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nėra sukurtų šablonų
            </div>
          ) : (
            <div className="grid gap-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => onSelect(template)}
                  className="w-full p-4 text-left bg-gray-50 hover:bg-[#0F7D70]/5 border border-gray-200 hover:border-[#0F7D70] rounded-lg transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-[#0F7D70]">
                        {formatMoney(calculateTotal(template.items || []))}
                      </p>
                      <p className="text-xs text-gray-500">
                        {template.items?.length || 0} prekių
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Atšaukti
          </button>
        </div>
      </div>
    </div>
  );
};

// Send Quote Modal
const SendQuoteModal = ({ quote, onClose, onSent }) => {
  const [toEmail, setToEmail] = useState(quote.client_snapshot?.contact_email || '');
  const [cc, setCc] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!toEmail) {
      toast.error('Įveskite gavėjo el. paštą');
      return;
    }
    
    setSending(true);
    try {
      const ccList = cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [];
      
      await sendQuote(quote.id, {
        to_email: toEmail,
        message: message || null,
        cc: ccList.length > 0 ? ccList : null
      });
      
      toast.success('Pasiūlymas išsiųstas sėkmingai');
      onSent();
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Send size={20} className="text-[#0F7D70]" />
            Siųsti pasiūlymą
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSend} className="p-4 space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Pasiūlymas:</strong> {quote.number}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Klientas:</strong> {quote.client_snapshot?.name}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gavėjo el. paštas *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CC (atskirti kableliu)
            </label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="el.pastas1@pvz.lt, el.pastas2@pvz.lt"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Žinutė (neprivaloma)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Papildoma žinutė gavėjui..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Jei tuščia, bus naudojamas standartinis tekstas.
            </p>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Atšaukti
            </button>
            <button
              type="submit"
              disabled={sending}
              className="flex-1 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Siunčiama...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Siųsti
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QdQuoteEditor;
